import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  ArrowRightIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { fetchHourSettings } from "../../../api/hour";
import { fetchAbsenceTypes } from "../../../api/absence/type";
import { fetchWorkSchedules } from "../../../api/workSchedules";

const GuideTaskRow = ({ icon: Icon, title, status, description, to, linkLabel }) => {
  const tone =
    status === "ok"
      ? "border-emerald-200 bg-emerald-50/50"
      : status === "warn"
        ? "border-amber-200 bg-amber-50/40"
        : "border-slate-200 bg-slate-50/60";

  const StatusIcon =
    status === "ok" ? CheckCircleIcon : status === "warn" ? ExclamationTriangleIcon : InformationCircleIcon;

  const statusColor =
    status === "ok" ? "text-emerald-700" : status === "warn" ? "text-amber-800" : "text-slate-600";

  return (
    <div className={`flex flex-col gap-3 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${tone}`}>
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className={`mt-0.5 flex items-center gap-1.5 text-xs font-medium ${statusColor}`}>
            <StatusIcon className="h-4 w-4 shrink-0" />
            <span>{description}</span>
          </div>
        </div>
      </div>
      {to ? (
        <Link
          to={to}
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
        >
          {linkLabel}
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      ) : null}
    </div>
  );
};

/**
 * Oppstartsstatus / sjekkliste for timer-modulen. Skal vises på relevant side * (f.eks. brukerguide timer), ikke på dashbordets forside.
 */
export const CompanyGuideSection = () => {
  const { t } = useTranslation();
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const [hourSettings, setHourSettings] = useState(null);
  const [absenceTypeCount, setAbsenceTypeCount] = useState(null);
  const [workScheduleMeta, setWorkScheduleMeta] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const hoursModuleOn =
    !Array.isArray(currentCompany?.active_modules) ||
    currentCompany.active_modules.length === 0 ||
    currentCompany.active_modules.includes("hours");

  useEffect(() => {
    if (!hoursModuleOn) return;

    let cancelled = false;

    const run = async () => {
      setLoadError(false);
      try {
        const [hsRes, atRes] = await Promise.all([
          fetchHourSettings(),
          fetchAbsenceTypes({ page: 1, per_page: 1, sortBy: "created_at", sort: "DESC" }),
        ]);
        if (cancelled) return;
        const hsData =
          hsRes?.data?.data !== undefined
            ? hsRes.data.data
            : hsRes?.data !== undefined
              ? hsRes.data
              : {};
        setHourSettings(typeof hsData === "object" ? hsData : {});
        const total = atRes?.totalCount ?? atRes?.data?.totalCount;
        setAbsenceTypeCount(typeof total === "number" ? total : null);
        let wsMeta = null;
        try {
          const wsRes = await fetchWorkSchedules();
          wsMeta = wsRes?.meta && typeof wsRes.meta === "object" ? wsRes.meta : null;
        } catch {
          wsMeta = null;
        }
        if (cancelled) return;
        setWorkScheduleMeta(wsMeta);
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setHourSettings(null);
          setAbsenceTypeCount(null);
          setWorkScheduleMeta(null);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [hoursModuleOn]);

  const tasks = useMemo(() => {
    const hasHoursHandler = Boolean(hourSettings?.hours_handler_id ?? hourSettings?.hours_handler?.id);
    const hasAbsenceHandler = Boolean(
      hourSettings?.absence_handler_id ?? hourSettings?.absence_handler?.id
    );
    const typesOk = absenceTypeCount === null ? null : absenceTypeCount > 0;

    const sc = workScheduleMeta?.schedules_count;
    const un = workScheduleMeta?.unassigned_employee_count;
    let workScheduleStatus = "info";
    let workScheduleDescription = t("company_guide.hours.task.work_schedule.info");
    if (hourSettings !== null && workScheduleMeta != null) {
      if (sc === 0) {
        workScheduleStatus = "warn";
        workScheduleDescription = t("company_guide.hours.task.work_schedule.warn_none");
      } else if (un > 0) {
        workScheduleStatus = "warn";
        workScheduleDescription = t("company_guide.hours.task.work_schedule.warn_unassigned", { count: un });
      } else {
        workScheduleStatus = "ok";
        workScheduleDescription = t("company_guide.hours.task.work_schedule.ok");
      }
    }

    return [
      {
        id: "registration_model",
        icon: ClockIcon,
        title: t("company_guide.hours.task.registration_model.title"),
        status: "info",
        description: t("company_guide.hours.task.registration_model.info"),
        to: "/dashboard/guide/hours#registration",
        linkLabel: t("company_guide.hours.link.scroll_to_explanation"),
      },
      {
        id: "absence_types",
        icon: ClockIcon,
        title: t("company_guide.hours.task.absence_types.title"),
        status: typesOk === null ? "info" : typesOk ? "ok" : "warn",
        description:
          typesOk === null
            ? t("company_guide.hours.task.absence_types.unknown")
            : typesOk
              ? t("company_guide.hours.task.absence_types.ok", { count: absenceTypeCount })
              : t("company_guide.hours.task.absence_types.warn"),
        to: "/dashboard/hours/settings#guide-hours-absence-types",
        linkLabel: t("company_guide.hours.link.settings_section"),
      },
      {
        id: "handlers",
        icon: ClockIcon,
        title: t("company_guide.hours.task.handlers.title"),
        status: hasHoursHandler && hasAbsenceHandler ? "ok" : "warn",
        description:
          hasHoursHandler && hasAbsenceHandler
            ? t("company_guide.hours.task.handlers.ok")
            : t("company_guide.hours.task.handlers.warn"),
        to: "/dashboard/hours/settings#guide-hours-handlers",
        linkLabel: t("company_guide.hours.link.settings_section"),
      },
      {
        id: "work_schedule",
        icon: ClockIcon,
        title: t("company_guide.hours.task.work_schedule.title"),
        status: workScheduleStatus,
        description: workScheduleDescription,
        to: "/dashboard/hours/work-schedules",
        linkLabel: t("company_guide.hours.link.work_schedule_admin"),
      },
    ];
  }, [hourSettings, absenceTypeCount, workScheduleMeta, t]);

  const doneCount = useMemo(
    () => tasks.filter((row) => row.status === "ok").length,
    [tasks]
  );
  const warnCount = useMemo(
    () => tasks.filter((row) => row.status === "warn").length,
    [tasks]
  );

  if (!hoursModuleOn) {
    return null;
  }

  return (
    <section
      id="setup-status"
      className="scroll-mt-24 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5"
    >
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 shadow-sm">
            <BookOpenIcon className="h-4 w-4" />
            {t("user_guide.hours.checklist.badge")}
          </div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-950">
            {t("user_guide.hours.checklist.heading")}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">{t("user_guide.hours.checklist.subtitle")}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
            {t("company_guide.hours.progress_label")}
          </div>
          <div className="mt-1 font-semibold text-slate-900">
            {loadError
              ? t("company_guide.hours.progress_error")
              : hourSettings === null
                ? t("company_guide.hours.progress_loading")
                : warnCount > 0
                  ? t("company_guide.hours.progress_summary_warnings", {
                      done: doneCount,
                      total: tasks.filter((r) => r.status !== "info").length,
                      warnings: warnCount,
                    })
                  : t("company_guide.hours.progress_summary_ok", {
                      done: doneCount,
                      total: tasks.filter((r) => r.status !== "info").length,
                    })}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {tasks.map((row) => (
          <GuideTaskRow key={row.id} {...row} />
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
        <p>{t("company_guide.hours.footer_hint")}</p>
        <p className="mt-2">
          <Link
            to="/dashboard/hours/settings#guide-hours-intro"
            className="font-semibold text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-500"
          >
            {t("company_guide.hours.footer_link_settings")}
          </Link>
        </p>
      </div>
    </section>
  );
};
